import { useCallback } from "react";
import { toastManager } from "@salestrak/ui";

import { useTransientFlag } from "#app/lib/hooks/use-transient-value";
import { copyToClipboard } from "#app/lib/utils/copy-to-clipboard";

/**
 * Copies `text` and toasts the outcome. Resolves to whether the copy landed,
 * so a caller can pair the toast with its own in-place feedback.
 */
export async function copyText(text: string, what: string): Promise<boolean> {
  const ok = await copyToClipboard(text);
  if (ok) toastManager.add({ title: `${what} copied`, type: "success" });
  else
    toastManager.add({
      title: `Failed to copy ${midSentence(what)}`,
      type: "error",
    });
  return ok;
}

// "Address" → "address" mid-sentence, but an acronym keeps its caps: "URL",
// "API key", "Webhook ID" → "webhook ID". Only a word whose second letter is
// lowercase is a capitalized common noun.
export function midSentence(what: string): string {
  const second = what.charAt(1);
  if (second && second === second.toLowerCase()) {
    return what.charAt(0).toLowerCase() + what.slice(1);
  }
  return what;
}

/** How long a copy control shows its check before it reads "Copy" again. */
export const COPY_FEEDBACK_MS = 2000;

/**
 * `copyText` plus the control's own in-place feedback: the toast lands in a
 * corner, the check lands under the pointer and says WHICH control acted.
 */
export function useCopyFeedback(): {
  copied: boolean;
  copy: (text: string, what: string) => Promise<boolean>;
} {
  const [copied, flash] = useTransientFlag(COPY_FEEDBACK_MS);
  const copy = useCallback(
    async (text: string, what: string) => {
      const ok = await copyText(text, what);
      if (ok) flash();
      return ok;
    },
    [flash],
  );
  return { copied, copy };
}

/** Opens `href` in a new tab. `noopener`: the new tab gets no handle back to
 *  this one. */
export function openHref(href: string) {
  if (typeof window !== "undefined") window.open(href, "_blank", "noopener");
}
